import * as React from 'react'
import {useGLTranslation, useMouseover} from './hooks'
import {BreakStyle, defineStylesWithBreaks, useThemedStylesWithBreaks} from './styles/style'
import {useLocalizedFetchOptions} from './utils/fetch'
import {pagePadding} from './shared'
import {GLTranslate} from './GLTranslate'

interface UnverifiedBannerProps {
  appearance?: 'default' | 'centered' | 'centered-with-top-padding'
  email: string
  apiUrl: string
  postFn: (host: string, endpoint: string, postOptions: RequestInit, body?: BodyInit | null) => void
}

type ResendState = 'initial' | 'sending' | 'sent' | 'error'

export function UnverifiedBanner({appearance = 'default', email, postFn, apiUrl}: UnverifiedBannerProps) {
  const [resendState, setResendState] = React.useState<ResendState>('initial')
  const [mouseOver, mouseOverProps] = useMouseover()
  const s = useThemedStylesWithBreaks(bannerStyles)
  const {t} = useGLTranslation()
  const postOptions = useLocalizedFetchOptions('application/json')

  const onResend = React.useCallback(async () => {
    if (resendState === 'sent') {
      return
    }
    setResendState('sending')
    try {
      await postFn(apiUrl, '/verify/resend', postOptions, JSON.stringify({email}))
      setResendState('sent')
    } catch (err) {
      console.log(err)
      setResendState('error')
    }
  }, [resendState, postOptions, email, postFn, apiUrl])

  let content: React.ReactNode = null
  switch (resendState) {
    case 'sending':
      content = <span style={s.sending}>{t('unverifiedBannerSending')}</span>
      break
    case 'sent':
      content = <span>{t('unverifiedBannerSent')}</span>
      break
    case 'error':
      content = <span>{t('unverifiedBannerError')}</span>
      break
    case 'initial':
    default:
      content = (
        <span>
          <GLTranslate i18nKey="unverifiedBannerInitial">
            ⚠️ For full access, please verify your email or
            <span {...mouseOverProps} style={mouseOver ? s.buttonHover : s.button} onClick={onResend}>
              click here to have it sent again.
            </span>
          </GLTranslate>
        </span>
      )
  }

  return (
    <div
      style={
        appearance === 'centered'
          ? s.containerCentered
          : appearance === 'centered-with-top-padding'
            ? s.containerCenteredWithTopMargin
            : s.container
      }
    >
      {content}
    </div>
  )
}

const bannerStyles = defineStylesWithBreaks((theme, font) => {
  const container: BreakStyle = {
    ...font.body,
    textAlign: 'end',
    paddingLeft: '12px',
    paddingRight: '12px'
  }

  const containerCentered: BreakStyle = {...container, textAlign: 'center'}

  const buttonColor: BreakStyle = {color: theme.accent.base}

  const button: BreakStyle = {
    ...font.body,
    ...buttonColor,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  }

  return {
    container,
    containerCentered,
    containerCenteredWithTopMargin: {
      ...containerCentered,
      paddingTop: pagePadding.paddingTop
    },
    button,
    buttonHover: {
      ...button,
      color: theme.accent.hover
    },
    sending: buttonColor
  }
})
